export class Queue<T> {
	private items: T[];

	constructor() {
		this.items = [];
	}

	// Add element to the front of the queue
	pushFront(element: T): void {
		this.items.unshift(element);
	}

	// Add element to the end of the queue
	pushEnd(element: T): void {
		this.items.push(element);
	}

	// Remove and return element from the front of the queue
	popFront(): T | undefined {
		if (this.isEmpty()) {
			return undefined;
		}
		return this.items.shift();
	}

	// Remove and return element from the end of the queue
	popEnd(): T | undefined {
		if (this.isEmpty()) {
			return undefined;
		}
		return this.items.pop();
	}

	// Get the length of the queue
	len(): number {
		return this.items.length;
	}

	// Check if queue is empty
	isEmpty(): boolean {
		return this.items.length === 0;
	}

	// Peek at the front element without removing it
	peekFront(): T | undefined {
		if (this.isEmpty()) {
			return undefined;
		}
		return this.items[0];
	}

	// Peek at the end element without removing it
	peekEnd(): T | undefined {
		if (this.isEmpty()) {
			return undefined;
		}
		return this.items[this.items.length - 1];
	}

	// Clear the queue
	clear(): void {
		this.items = [];
	}

	// Get all items as array
	toArray(): T[] {
		return [...this.items];
	}
}

interface PromiseQueueEntrie<Res> {
	task: () => Promise<Res>;
	signal: AbortSignal;
	resolvePromise: Promise<Res>;
	resolve: (val: Res) => void;
	reject: (e: unknown) => void;
}

export class PromiseQueue<Res> {
	queue = new Queue<PromiseQueueEntrie<Res>>();
	isProcessing = false;
	pushTask(task: () => Promise<Res>, signal: AbortSignal): Promise<Res> {
		const newTask = {
			task,
			signal: signal
		} as PromiseQueueEntrie<Res>;
		newTask.resolvePromise = new Promise<Res>((res, rej) => {
			newTask.resolve = res;
			newTask.reject = rej;
		});
		signal.addEventListener("abort", (e) => {
			newTask.reject(e);
		});

		this.queue.pushEnd(newTask);
		this.startProcess();

		return newTask.resolvePromise;
	}

	async startProcess() {
		if (this.isProcessing) return;
		this.isProcessing = true;
		while (this.queue.len() > 0) {
			const task = this.queue.popFront();
			if (!task || task?.signal.aborted) continue;
			try {
				const val = await task.task();
				task.resolve(val);
			} catch (e) {
				task.reject(e);
			}
		}
		this.isProcessing = false;
	}
}
